<template>
  <smd-one-col-layout>
    <h3 class="ta-center mb-m">Enter last 8 characters of your link</h3>

    <p v-if="error" class="ta-center mb-m error">{{ error }}</p>

    <form @submit.prevent="verifyInviteCode" novalidate>
      <div class="smd-form__row">
        <div class="smd-form__col">
          <span>https://hi.nowserving.ph/</span>
          <input type="text"
                 maxlength="8"
                 v-model.trim="inviteCode">
        </div>
      </div>

      <div class="smd-form__row justify-content-center">
        <vue-hcaptcha :sitekey="$options.siteKey"
                      ref="hcaptcha"
                      size="invisible"
                      @error="onHcaptchaError"
                      @verify="onHcaptchaVerify">
        </vue-hcaptcha>
      </div>

      <smd-btn class="d-block m-auto"
               section="4"
               type="submit"
               :disabled="submitDisabled"
               :is-loading="submitting"
               :variant="submitDisabled ? 'blue-disabled': 'blue-enabled'">
        Verify Link
      </smd-btn>
    </form>
  </smd-one-col-layout>
</template>

<script>
  import {bus} from '@/main';
  import {AccountService, CLAIM_REQUEST_RESULT} from '@/services/account';
  import {OtpService, CLAIM_OTP_RESPONSE_ENUM} from '@/services/otp';
  import {
    SESSION_EXPIRED,
    CLAIM_NO_CONTACT_INFO_MESSAGE,
    CLAIM_INVALID_INVITE_CODE_MESSAGE,
    CLAIM_NOT_CLAIMED_MESSAGE,
    CLAIM_RECENT_VERIFICATION_MESSAGE,
    CLAIM_CLAIM_FAILED_MESSAGE,
    CLAIM_ALREADY_CLAIMED_MESSAGE,
  } from '@/constants/messages';
  import {
    isEmpty,
    claimSentToMobileNumberText,
  } from '@/utils';
  import {
    SHOW_OTP_EVENT,
    CLOSE_OTP_EVENT,
    OTP_SUCCESS_EVENT,
    SHOW_TOS,
    CLOSE_TOS,
  } from '@/constants/bus';
  import ToastMixin from '@/mixins/toast-mixin';
  import hcaptchaMixin from '@/mixins/hcaptcha-mixin';
  import {logger, redact, trackEvent} from '@/utils/logger';
  import GTAG_EVENT_ACTION from '@/reference/gtag-event-actions-lookup';
  import {GTAG_PARAMS_ORIGIN, GTAG_PARAMS_TYPE} from '@/reference/gtag-params-lookup';

  const TOKEN_SOURCE = {
    INITIAL_TOKEN: 'initialToken',
    VERIFY_INVITE_CODE: 'verifyInviteCode',
  };

  export default {
    name: 'InviteCodeModal',
    mixins: [
      hcaptchaMixin,
      ToastMixin,
    ],
    props: {
      initialToken: {type: String, default: null},
    },
    data() {
      return {
        submitting: false,
        inviteCode: '',
        token: this.initialToken,
        tokenSource: this.initialToken ? TOKEN_SOURCE.INITIAL_TOKEN : null,
        error: null,
      };
    },
    computed: {
      submitDisabled() {
        return this.inviteCode === '' || this.submitting;
      },
    },
    created() {
      logger.info('InviteCodeModal created', {
        initialToken: redact(this.initialToken),
        tokenSource: this.tokenSource,
      });

      if (this.token) {
        bus.$emit(SHOW_TOS);
      }
      bus.$once(OTP_SUCCESS_EVENT, () => {
        this.onVerifySuccess();
      });

      bus.$once(CLOSE_TOS, (tosAccepted) => {
        console.log('tosAccepted', tosAccepted)

        if (tosAccepted) {
          this.claimInviteToken();
        } else {
          this.$modal.close();
        }
      });
    },
    methods: {
      otpModalDefaultProps() {
        const {
          NO_CONTACT_INFO,
          INVALID_INVITE_CODE,
          NEED_RELOGIN,
          NOT_CLAIMED,
          RECENT_VERIFICATION,
          CLAIM_FAILED,
          SUCCESS,
          PENDING_VERIFICATION,
          ALREADY_CLAIMED,
        } = CLAIM_OTP_RESPONSE_ENUM.RESEND;
        const {
          CLAIM_INVALID,
          CLAIM_EXPIRED,
          CLAIM_FAILED: VERIFY_CLAIM_FAILED,
          CLAIM_SUCCESS,
        } = CLAIM_OTP_RESPONSE_ENUM.VERIFY;

        return {
          verifyApiService: OtpService.claimVerify,
          resendApiService: OtpService.claimResend,
          successResendCodes: [SUCCESS, PENDING_VERIFICATION],
          failedResendCodes: {
            [NO_CONTACT_INFO]: CLAIM_NO_CONTACT_INFO_MESSAGE,
            [INVALID_INVITE_CODE]: CLAIM_INVALID_INVITE_CODE_MESSAGE,
            [NEED_RELOGIN]: SESSION_EXPIRED,
            [NOT_CLAIMED]: CLAIM_NOT_CLAIMED_MESSAGE,
            [RECENT_VERIFICATION]: CLAIM_RECENT_VERIFICATION_MESSAGE,
            [CLAIM_FAILED]: CLAIM_CLAIM_FAILED_MESSAGE,
            [ALREADY_CLAIMED]: CLAIM_ALREADY_CLAIMED_MESSAGE,
          },
          successVerifyCodes: [CLAIM_SUCCESS],
          failedVerifyCodes: {
            [CLAIM_INVALID]: 'Invite code invalid.',
            [CLAIM_EXPIRED]: 'Invite code expired.',
            [VERIFY_CLAIM_FAILED]: 'Invite code claim failed.',
          },
        };
      },
      async verifyInviteCode() {
        if (this.inviteCode.length !== 8) {
          this.error = 'Invite code must have 8 characters.';
          return;
        }

        this.error = null;
        this.submitting = true;

        try {
          const {data: {token, result}} = await AccountService.getInviteCodeToken(this.inviteCode);

          this.submitting = false;

          logger.info('getInviteCodeToken response', {
            result,
            token: redact(token),
          });

          if (result !== 1) {
            this.error = 'Invite code invalid.';
          } else {
            if (!token) {
              logger.error('getInviteCodeToken returned success but token is empty', {
                result,
                token: redact(token),
              });
            }
            this.token = token;
            this.tokenSource = TOKEN_SOURCE.VERIFY_INVITE_CODE;
            bus.$emit(SHOW_TOS);
          }
        } catch (error) {
          this.submitting = false;
          this.error = 'An unexpected error occured in claiming invite code.';
        }
      },
      postHcaptchaVerify() {
        this.claimInviteToken();
      },
      async claimInviteToken() {
        this.submitting = true;

        if (!this.validateToken) {
          this.triggerCaptcha();
          return;
        }

        logger.info('claim/request payload', {
          patienttoken: redact(this.token),
          captchatoken: redact(this.validateToken),
          tokenSource: this.tokenSource,
          inviteCodeLength: this.inviteCode ? this.inviteCode.length : 0,
          hasValidateToken: !!this.validateToken,
        });

        this.error = null;

        try {
          const {data: claimResponseData} = await AccountService.claimRequest({
            patienttoken: this.token,
            captchatoken: this.validateToken,
            agent: 'web3',
          });

          this.submitting = false;
          this.handleClaimResponse(claimResponseData);
        } catch (error) {
          this.submitting = false;
          this.error = 'An unexpected error occured in claiming invite code.';
        } finally {
          // Treat captcha tokens as single-use: never resend one after claim/request has seen it.
          this.validateToken = null;
        }
      },
      closeModal() {
        this.$modal.close(true);
      },
      handleClaimResponse(claimResponseData) {
        logger.info('claim/request response received', {
          result: claimResponseData.result,
          status: claimResponseData.status,
          hasClaimVerifyToken: redact(claimResponseData.claimverifytoken),
          hasVerifyToken: redact(claimResponseData.verifytoken),
          hasPartialNum: !!claimResponseData.partialnum,
          hasPartialEmail: !!claimResponseData.partialemail,
          responseKeys: Object.keys(claimResponseData),
        });

        if (claimResponseData.status === -9) {
          this.error = 'Unable to verify your invite code. Please try again later.';
          return;
        }

        const {
          NO_CONTACT_INFO,
          INVALID_INVITE_CODE,
          NEED_RELOGIN,
          NOT_CLAIMED,
          RECENT_VERIFICATION,
          CLAIM_FAILED,
          SUCCESS,
          PENDING_VERIFICATION,
          ALREADY_CLAIMED,
        } = CLAIM_OTP_RESPONSE_ENUM.RESEND;
        const {
          CLAIM_INVALID,
          CLAIM_EXPIRED,
          CLAIM_FAILED: VERIFY_CLAIM_FAILED,
          CLAIM_SUCCESS,
        } = CLAIM_OTP_RESPONSE_ENUM.VERIFY;
        const otpProps = {
          verifyApiService: OtpService.claimVerify,
          resendApiService: OtpService.claimResend,
          successResendCodes: [SUCCESS, PENDING_VERIFICATION],
          failedResendCodes: {
            [NO_CONTACT_INFO]: CLAIM_NO_CONTACT_INFO_MESSAGE,
            [INVALID_INVITE_CODE]: CLAIM_INVALID_INVITE_CODE_MESSAGE,
            [NEED_RELOGIN]: SESSION_EXPIRED,
            [NOT_CLAIMED]: CLAIM_NOT_CLAIMED_MESSAGE,
            [RECENT_VERIFICATION]: CLAIM_RECENT_VERIFICATION_MESSAGE,
            [CLAIM_FAILED]: CLAIM_CLAIM_FAILED_MESSAGE,
            [ALREADY_CLAIMED]: CLAIM_ALREADY_CLAIMED_MESSAGE,
          },
          successVerifyCodes: [CLAIM_SUCCESS],
          failedVerifyCodes: {
            [CLAIM_INVALID]: 'Invite code invalid.',
            [CLAIM_EXPIRED]: 'Invite code expired.',
            [VERIFY_CLAIM_FAILED]: 'Invite code claim failed.',
          },
          sentTo: isEmpty(claimResponseData.partialnum) ? claimResponseData.partialemail : claimSentToMobileNumberText(claimResponseData.partialnum),
          resendRequestBody: {
            patienttoken: this.token,
          },
        };

        switch (claimResponseData.result) {
          case CLAIM_REQUEST_RESULT.NO_CONTACT_INFO:
            this.error = CLAIM_NO_CONTACT_INFO_MESSAGE;
            break;
          case CLAIM_REQUEST_RESULT.INVALID_INVITE_CODE:
            this.error = CLAIM_INVALID_INVITE_CODE_MESSAGE;
            break;
          case CLAIM_REQUEST_RESULT.NEED_RELOGIN:
            this.error = SESSION_EXPIRED;
            break;
          case CLAIM_REQUEST_RESULT.NOT_CLAIMED:
            this.error = CLAIM_NOT_CLAIMED_MESSAGE;
            break;
          case CLAIM_REQUEST_RESULT.RECENT_VERIFICATION:
            logger.warn('OTP modal opened without verifyToken (RECENT_VERIFICATION path)', {
              result: claimResponseData.result,
            });
            bus.$emit(SHOW_OTP_EVENT, {
              ...otpProps,
              initialError: CLAIM_RECENT_VERIFICATION_MESSAGE,
            });
            break;
          case CLAIM_REQUEST_RESULT.CLAIM_FAILED:
            this.error = CLAIM_CLAIM_FAILED_MESSAGE;
            break;
          case CLAIM_REQUEST_RESULT.SUCCESS:
            otpProps.initialVerifyToken = claimResponseData.claimverifytoken || claimResponseData.verifytoken;
            logger.info('OTP modal opened (SUCCESS path)', {
              verifyTokenSource: claimResponseData.claimverifytoken
                ? 'claimverifytoken'
                : (claimResponseData.verifytoken ? 'verifytoken' : 'NONE'),
              initialVerifyToken: redact(otpProps.initialVerifyToken),
            });
            bus.$emit(SHOW_OTP_EVENT, otpProps);
            break;
          case CLAIM_REQUEST_RESULT.PENDING_VERIFICATION:
            logger.info('OTP modal opened (PENDING_VERIFICATION path)', {
              verifyToken: redact(claimResponseData.verifytoken),
            });
            bus.$emit(SHOW_OTP_EVENT, {
              ...otpProps,
              initialVerifyToken: claimResponseData.verifytoken,
            });
            break;
          case CLAIM_REQUEST_RESULT.ALREADY_CLAIMED:
            this.error = CLAIM_ALREADY_CLAIMED_MESSAGE;
            this.closeModal();
            this.showToast({text: CLAIM_ALREADY_CLAIMED_MESSAGE});
            break;
          default:
            this.error = 'An unexpected error occured in claiming invite code.';
            break;
        }
      },
      onVerifySuccess() {
        const claimPayload = {
          type: this.tokenSource === TOKEN_SOURCE.INITIAL_TOKEN
            ? GTAG_PARAMS_TYPE.InviteLink
            : GTAG_PARAMS_TYPE.InviteCode,
          source: GTAG_PARAMS_ORIGIN.NswebDashboard,
        };

        this.$gtag.event(GTAG_EVENT_ACTION.Claim, claimPayload);
        trackEvent(GTAG_EVENT_ACTION.Claim, claimPayload);
        bus.$emit(CLOSE_OTP_EVENT);
        this.closeModal();
      },
    },
  }
</script>

<style lang="scss" scoped>
  form {
    span {
      margin-bottom: 0 !important;
    }

    .smd-form__col {
      align-items: center;
      flex-direction: unset;
    }
  }
</style>
